import { lowerCase } from "lodash";

import { FlowVersionFlowChild, FlowVersionStatus } from "src/api/flowTypes";
import {
  StatusIcon,
  type StatusIconVariant,
} from "src/base-components/StatusIcon";
import { ReviewRequestStatus } from "src/clients/flow-api";
import { assertUnreachable } from "src/utils/typeUtils";

export type FeVersionStatusFilterValue =
  (typeof FeVersionStatusFilterOptions)[keyof typeof FeVersionStatusFilterOptions]["value"];
export const FeVersionStatusFilterOptions = {
  published: {
    label: "Published",
    value: FlowVersionStatus.PUBLISHED,
  } as const,
  inReview: { label: "In review", value: "InReview" } as const,
  drafts: { label: "Drafts", value: "DraftsNoReview" } as const,
  archived: { label: "Archived", value: FlowVersionStatus.ARCHIVED } as const,
};
export const matchesFilter = (
  version: FlowVersionFlowChild,
  filter: FeVersionStatusFilterValue,
) => {
  if (filter === "InReview") {
    return versionIsInReview(version);
  }
  if (filter === "DraftsNoReview") {
    return (
      version.status === FlowVersionStatus.DRAFT && !versionIsInReview(version)
    );
  }
  return version.status === filter;
};

export const getActiveVersions = (
  versions: FlowVersionFlowChild[],
  requiredVersionId?: string,
) =>
  versions.filter(
    (version) =>
      version.status !== FlowVersionStatus.ARCHIVED ||
      version.id === requiredVersionId,
  );

export const versionIsInReview = (
  versionPicks: Pick<FlowVersionFlowChild, "status" | "review">,
) =>
  versionPicks.status === FlowVersionStatus.DRAFT &&
  versionPicks.review &&
  versionPicks.review?.request_status !== ReviewRequestStatus.CANCELLED;

export const getVersionStatusColor = (
  version: Pick<FlowVersionFlowChild, "status" | "review">,
): StatusIconVariant => {
  switch (version.status) {
    case FlowVersionStatus.PUBLISHED:
      return "published";
    case FlowVersionStatus.ARCHIVED:
      return "archived";
    case FlowVersionStatus.DRAFT:
      if (versionIsInReview(version)) {
        return "review";
      }
      return "draft";
    default:
      return assertUnreachable(version.status);
  }
};

type StatusDotIconSize = "3xs" | "2xs" | "xs";

type VersionStatusDotIndicatorProps = {
  version: Pick<FlowVersionFlowChild, "status" | "review">;
  size?: StatusDotIconSize;
};

export const VersionStatusDotIndicator: React.FC<
  VersionStatusDotIndicatorProps
> = ({ size = "3xs", version }) => {
  const dataLocEnding = (() => {
    if (version.status !== FlowVersionStatus.DRAFT) {
      return lowerCase(version.status);
    }
    return versionIsInReview(version) ? "inreview" : lowerCase(version.status);
  })();
  return (
    <StatusIcon
      variant={getVersionStatusColor(version)}
      dataLoc={`version-status-${dataLocEnding}`}
      size={size}
    />
  );
};
